import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
// `react-transition-group` is pinned to 2.9.0, which resolves its child node via
// `ReactDOM.findDOMNode` (removed in React 19). A React-19 upgrade must therefore
// also move to react-transition-group v4 + the `nodeRef` API (see CLAUDE.md
// external references). This is why the Transition child below must stay a single
// real DOM element.
import Transition from 'react-transition-group/Transition';
import useEscapeToClose from '../hooks/useEscapeToClose';

/**
 * Enter/exit duration (ms). Mirrors react-bootstrap's `Modal.TRANSITION_DURATION`
 * (300). Backstop the underlying Transition uses to fire onEntered/onExited even
 * if the browser's `transitionend` is missed (e.g. `prefers-reduced-motion:
 * reduce`, where the dock SCSS sets `transition: none`). The visual slide itself
 * is the host's — see the Host DS contract in CLAUDE.md.
 */
const TRANSITION_DURATION = 300;

/**
 * Maps the Transition lifecycle status to the DS `in` class, exactly as
 * react-bootstrap's `Fade` did (ENTERING/ENTERED -> `in`). The host keys the
 * dialog slide off `&.fade.in` (see Host DS contract in CLAUDE.md), so the panel
 * always carries `fade` and gains `in` here while open.
 */
const STATUS_CLASS = {
	entering: 'in',
	entered: 'in'
};

/**
 * SlidePanel -- a keep-mounted, visibility-toggled slide-in panel portaled to
 * `document.body`.
 *
 * Replaces the react-bootstrap `Modal` for the drawer family. Unlike `Modal`
 * (which hard-unmounts its content on close, re-bootstrapping embedded fragment
 * widgets on every reopen), SlidePanel keeps its children mounted for the life
 * of the page and toggles the DS `in` class so the fragments never re-run.
 *
 * The slide is driven the same way react-bootstrap did it: this uses
 * `react-transition-group`'s `Transition` (the exact primitive react-bootstrap's
 * `Fade` was built on) to own the enter/exit timing and the timeout backstop,
 * but with `mountOnEnter`/`unmountOnExit` OFF so the panel stays mounted. The
 * host owns the actual transform slide (see Host DS contract in CLAUDE.md).
 *
 * SlidePanel does NOT own scroll-lock (hoisted to a single shared consumer) or
 * focus-trapping (callers keep their own `useFocusTrap`). It owns portaling, the
 * enter/exit transition, Escape + outside-click close (via a dismiss scrim), and
 * `inert` when it is not the active surface.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string} props.dockClass - 'dock-left' or 'dock-right'.
 * @param {Function} props.onClose - Called on Escape / dismiss-scrim click.
 * @param {string} [props.ariaLabel]
 * @param {string} [props.baseClassName] - DS scoping class (main panel defaults
 *   to `ws-nav-drawer-modal`; drilldown passes `ws-nav-drawer-drilldown`).
 * @param {string} [props.className]
 * @param {boolean} [props.isActive] - Whether this is the topmost open surface.
 *   When false the panel is `inert`. Both production callers (MainPanel,
 *   DrilldownPanel) pass this explicitly; the `null` default that falls back to
 *   `isOpen` exists only for tests / standalone use.
 * @param {Function} [props.onEntered]
 * @param {Function} [props.onExited]
 * @param {React.ReactNode} [props.backdrop] - Optional visual backdrop element
 *   rendered as a sibling. The drilldown omits it (the main panel's backdrop
 *   already dims behind both) but still closes on outside-click via its scrim.
 * @param {string} [props.dataTestId]
 * @param {React.ReactNode} props.children
 * @returns {React.ReactPortal|null}
 */
const SlidePanel = ({
	isOpen,
	dockClass,
	onClose,
	ariaLabel,
	baseClassName,
	className,
	isActive,
	onEntered,
	onExited,
	backdrop,
	dataTestId,
	children
}) => {
	// See the isActive JSDoc: the null-sentinel default is a test/standalone
	// convenience; both production callers always pass isActive explicitly.
	const active = isActive == null ? isOpen : isActive;

	useEscapeToClose(active, onClose);

	if (typeof document === 'undefined') return null;

	return createPortal(
		<>
			{backdrop}
			{/* Dismiss scrim: a full-viewport sibling of the dialog painted behind
			    it, whose click closes the panel. It makes outside-click dismissal
			    work regardless of the host container width (`.dock-full` is NOT
			    full-viewport — see Host DS contract in CLAUDE.md). Being a SIBLING
			    of the dialog (not an ancestor) is what makes the missing
			    mousedown-guard safe: a text-selection drag that starts inside the
			    dialog and releases on the scrim resolves its `click` to the shared
			    portal ancestor (document.body), not the scrim, so it cannot
			    false-close. Interactivity is gated to active && open; closed or
			    beneath a stacked surface it is click-through. */}
			{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
				className="ws-nav-drawer__scrim"
				style={{
					pointerEvents: active && isOpen ? 'auto' : 'none'
				}}
				onClick={onClose}
				aria-hidden="true"
				data-testid="ws-nav-drawer-scrim"
			/>
			{/* Single DOM child: Transition (v2.9) resolves the node via
			    findDOMNode, so its child must be one real element -- the backdrop
			    and scrim are siblings outside the Transition. */}
			<Transition
				in={isOpen}
				timeout={TRANSITION_DURATION}
				mountOnEnter={false}
				unmountOnExit={false}
				appear={false}
				onEntered={onEntered}
				onExited={onExited}
			>
				{(status) => {
					const classes = [
						'modal',
						dockClass,
						'dock-full',
						'fade',
						baseClassName
					];
					if (className) classes.push(className);
					if (STATUS_CLASS[status])
						classes.push(STATUS_CLASS[status]);

					return (
						<div
							className={classes.join(' ')}
							role="dialog"
							aria-modal="true"
							aria-label={ariaLabel}
							// `inert` (not aria-hidden, which errors when an ancestor
							// of a focused control is hidden) marks a non-active
							// surface; callers move focus out before marking inactive.
							inert={active ? undefined : ''}
							data-testid={dataTestId}
						>
							<div className="modal-dialog">
								<div className="modal-content">{children}</div>
							</div>
						</div>
					);
				}}
			</Transition>
		</>,
		document.body
	);
};

SlidePanel.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	dockClass: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
	ariaLabel: PropTypes.string,
	baseClassName: PropTypes.string,
	className: PropTypes.string,
	isActive: PropTypes.bool,
	onEntered: PropTypes.func,
	onExited: PropTypes.func,
	backdrop: PropTypes.node,
	dataTestId: PropTypes.string,
	children: PropTypes.node.isRequired
};

SlidePanel.defaultProps = {
	ariaLabel: undefined,
	baseClassName: 'ws-nav-drawer-modal',
	className: '',
	isActive: null,
	onEntered: undefined,
	onExited: undefined,
	backdrop: null,
	dataTestId: 'ws-nav-drawer-slide-panel'
};

export { SlidePanel, TRANSITION_DURATION };
